import React, { useState, useCallback, useEffect } from 'react';
import {
  XIcon,
  PlusIcon,
} from 'lucide-react';
import FileDropzone from './FileDropzone';
import { Asset as ApiAsset, tenantApi } from '../lib/tenant-api';

interface AssetUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (assets: ApiAsset[]) => void;
}

interface FileWithPreview {
  file: File;
  id: string;
  preview?: string;
  error?: string;
  uploaded?: boolean;
  name: string;
  tags: string[];
}

const AssetUploadModal: React.FC<AssetUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
}) => {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  // Global tags (apply to all)
  const [globalTags, setGlobalTags] = useState<string[]>([]);
  const [globalTagInput, setGlobalTagInput] = useState('');
  const [showGlobalTagInput, setShowGlobalTagInput] = useState(false);

  // Per-file tag input tracking
  const [activeTagInput, setActiveTagInput] = useState<string | null>(null);
  const [perFileTagInput, setPerFileTagInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      const loadAvailableTags = async () => {
        try {
          const response = await tenantApi.getAssetTags();
          setAvailableTags(response.tags);
        } catch (error) {
          console.error('Failed to load available tags:', error);
        }
      };
      loadAvailableTags();
    }
  }, [isOpen]);

  // File validation
  const validateFile = (file: File): string | null => {
    const maxSize = {
      image: 10 * 1024 * 1024,
      video: 250 * 1024 * 1024,
      pdf: 250 * 1024 * 1024,
      audio: 250 * 1024 * 1024,
    };
    const allowedTypes = {
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
      video: ['video/mp4', 'video/webm', 'video/quicktime', 'video/avi'],
      pdf: ['application/pdf'],
      audio: ['audio/mp3', 'audio/wav', 'audio/ogg', 'audio/mpeg'],
    };

    let fileType: 'image' | 'video' | 'pdf' | 'audio' | null = null;
    if (allowedTypes.image.includes(file.type)) fileType = 'image';
    else if (allowedTypes.video.includes(file.type)) fileType = 'video';
    else if (allowedTypes.pdf.includes(file.type)) fileType = 'pdf';
    else if (allowedTypes.audio.includes(file.type)) fileType = 'audio';

    if (!fileType) {
      return 'File type not supported.';
    }
    if (file.size > maxSize[fileType]) {
      const maxSizeMB = maxSize[fileType] / (1024 * 1024);
      return `File size exceeds ${maxSizeMB}MB limit.`;
    }
    return null;
  };

  const handleFilesSelected = useCallback((selectedFiles: File[]) => {
    const newFiles: FileWithPreview[] = selectedFiles.map((file) => {
      const id = Math.random().toString(36).substr(2, 9);
      const error = validateFile(file);
      const baseName = file.name.replace(/\.[^/.]+$/, '');

      const fileWithPreview: FileWithPreview = {
        file,
        id,
        error: error || undefined,
        name: baseName,
        tags: [],
      };

      if (file.type.startsWith('image/') && !error) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFiles((prev) =>
            prev.map((f) => (f.id === id ? { ...f, preview: e.target?.result as string } : f))
          );
        };
        reader.readAsDataURL(file);
      }

      return fileWithPreview;
    });

    setFiles((prev) => [...prev, ...newFiles]);
    setUploadError(null);
  }, []);

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setUploadError(null);
  };

  const clearAll = () => {
    setFiles([]);
    setGlobalTags([]);
    setGlobalTagInput('');
    setShowGlobalTagInput(false);
    setUploadError(null);
  };

  const updateFileName = (id: string, name: string) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, name } : f)));
  };

  // Global tag functions
  const addGlobalTag = (tag?: string) => {
    const value = (tag || globalTagInput).trim().toLowerCase();
    if (value && !globalTags.includes(value)) {
      setGlobalTags((prev) => [...prev, value]);
      // Also add to all files
      setFiles((prev) =>
        prev.map((f) => ({
          ...f,
          tags: f.tags.includes(value) ? f.tags : [...f.tags, value],
        }))
      );
    }
    setGlobalTagInput('');
  };

  const removeGlobalTag = (tag: string) => {
    setGlobalTags((prev) => prev.filter((t) => t !== tag));
    // Also remove from all files
    setFiles((prev) =>
      prev.map((f) => ({
        ...f,
        tags: f.tags.filter((t) => t !== tag),
      }))
    );
  };

  // Per-file tag functions
  const addFileTag = (fileId: string, tag?: string) => {
    const value = (tag || perFileTagInput).trim().toLowerCase();
    if (value) {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId && !f.tags.includes(value) ? { ...f, tags: [...f.tags, value] } : f
        )
      );
    }
    setPerFileTagInput('');
  };

  const removeFileTag = (fileId: string, tag: string) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, tags: f.tags.filter((t) => t !== tag) } : f))
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
  };

  const handleUpload = async () => {
    const validFiles = files.filter((f) => !f.error);
    if (validFiles.length === 0) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const uploadedAssets: ApiAsset[] = [];

      for (const fileItem of validFiles) {
        const allTags = [...new Set([...fileItem.tags])];
        const asset = await tenantApi.uploadAsset(
          fileItem.file,
          undefined,
          allTags.length > 0 ? allTags : undefined,
          fileItem.name || undefined
        );
        uploadedAssets.push(asset);

        setFiles((prev) => prev.map((f) => (f.id === fileItem.id ? { ...f, uploaded: true } : f)));
      }

      onUpload(uploadedAssets);
      handleClose();
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError('Failed to upload files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setIsUploading(false);
    setUploadError(null);
    setGlobalTags([]);
    setGlobalTagInput('');
    setShowGlobalTagInput(false);
    setActiveTagInput(null);
    setPerFileTagInput('');
    onClose();
  };

  const validFiles = files.filter((f) => !f.error);
  const totalSize = validFiles.reduce((sum, f) => sum + f.file.size, 0);

  if (!isOpen) return null;

  const suggestedTags = availableTags.filter((t) => !globalTags.includes(t)).slice(0, 4);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />

      {/* Panel */}
      <div className="relative bg-white w-full max-w-2xl max-h-[90vh] flex flex-col rounded shadow-xl mx-4">
        {/* Header */}
        <div className="px-8 pt-8 pb-4 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Upload Asset</h2>
            <p className="mt-1 text-sm text-gray-500">Upload images to your asset library</p>
          </div>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600">
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-8 pb-4 space-y-6">
          {/* Error */}
          {uploadError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-600">{uploadError}</p>
            </div>
          )}

          {/* Dropzone */}
          <FileDropzone
            onFilesSelected={handleFilesSelected}
            disabled={isUploading}
            label={files.length > 0 ? 'Drag more images' : 'Drag and drop image here'}
          />

          {/* Summary line */}
          {files.length > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-900">
                {validFiles.length} Asset{validFiles.length !== 1 ? 's' : ''} uploaded{' '}
                <span className="text-gray-500">• {formatFileSize(totalSize)}</span>
              </p>
              <button
                onClick={clearAll}
                className="text-sm text-gray-500 hover:text-gray-700"
                disabled={isUploading}
              >
                Clear all
              </button>
            </div>
          )}

          {/* Apply tag to all (shown when multiple files) */}
          {files.length > 1 && (
            <div className="border border-gray-200 rounded p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-indigo-500" />
                <p className="text-sm font-medium text-gray-900">
                  Apply tag to all assets at once -{' '}
                  <span className="font-normal text-gray-500">
                    The tag(s) will be added to all assets
                  </span>
                </p>
              </div>

              {/* Global tags + input */}
              <div className="flex flex-wrap items-center gap-2">
                {globalTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                  >
                    {tag}
                    <button
                      onClick={() => removeGlobalTag(tag)}
                      className="ml-1 text-indigo-500 hover:text-indigo-700"
                      disabled={isUploading}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {showGlobalTagInput ? (
                  <input
                    type="text"
                    value={globalTagInput}
                    onChange={(e) => setGlobalTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addGlobalTag();
                      }
                    }}
                    onBlur={() => {
                      if (globalTagInput.trim()) addGlobalTag();
                      setShowGlobalTagInput(false);
                    }}
                    placeholder="Enter tag name"
                    className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 w-28"
                    autoFocus
                    disabled={isUploading}
                  />
                ) : (
                  <button
                    onClick={() => setShowGlobalTagInput(true)}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                    disabled={isUploading}
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                    Add Tag
                  </button>
                )}
              </div>

              {/* Suggestions */}
              {suggestedTags.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-500">Suggestions:</span>
                  {suggestedTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => addGlobalTag(tag)}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                      disabled={isUploading}
                    >
                      <PlusIcon className="h-2.5 w-2.5 mr-0.5" />
                      {tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* File cards */}
          {files.map((fileItem) => {
            const fileSuggestions = availableTags
              .filter((t) => !fileItem.tags.includes(t))
              .slice(0, 4);

            return (
              <div
                key={fileItem.id}
                className={`border rounded p-4 ${fileItem.error ? 'border-red-200 bg-red-50' : 'border-gray-200'
                  }`}
              >
                <div className="flex gap-4">
                  {/* Thumbnail */}
                  <div className="flex-shrink-0 w-28 h-28 bg-gray-100 rounded overflow-hidden">
                    {fileItem.preview ? (
                      <img
                        src={fileItem.preview}
                        alt={fileItem.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-xs font-medium uppercase">
                          {fileItem.file.name.split('.').pop()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-1">
                        <label className="text-xs font-medium text-gray-700">Asset Name</label>
                        <input
                          type="text"
                          value={fileItem.name}
                          onChange={(e) => updateFileName(fileItem.id, e.target.value)}
                          className="block w-full max-w-xs px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          disabled={isUploading}
                        />
                        <p className="text-xs text-gray-400">{formatFileSize(fileItem.file.size)}</p>
                      </div>
                      <button
                        onClick={() => removeFile(fileItem.id)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        disabled={isUploading}
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>

                    {fileItem.error && (
                      <p className="text-xs text-red-600">{fileItem.error}</p>
                    )}

                    {!fileItem.error && (
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-700">Tags</label>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {fileItem.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                            >
                              {tag}
                              <button
                                onClick={() => removeFileTag(fileItem.id, tag)}
                                className="ml-1 text-indigo-500 hover:text-indigo-700"
                                disabled={isUploading}
                              >
                                <XIcon className="h-2.5 w-2.5" />
                              </button>
                            </span>
                          ))}
                          {activeTagInput === fileItem.id ? (
                            <input
                              type="text"
                              value={perFileTagInput}
                              onChange={(e) => setPerFileTagInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ',') {
                                  e.preventDefault();
                                  addFileTag(fileItem.id);
                                }
                              }}
                              onBlur={() => {
                                if (perFileTagInput.trim()) addFileTag(fileItem.id);
                                setActiveTagInput(null);
                                setPerFileTagInput('');
                              }}
                              placeholder="Enter tag name"
                              className="px-2 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 w-28"
                              autoFocus
                              disabled={isUploading}
                            />
                          ) : (
                            <button
                              onClick={() => {
                                setActiveTagInput(fileItem.id);
                                setPerFileTagInput('');
                              }}
                              className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                              disabled={isUploading}
                            >
                              <PlusIcon className="h-2.5 w-2.5 mr-0.5" />
                              Add Tag
                            </button>
                          )}
                        </div>

                        {/* File-level suggestions */}
                        {fileSuggestions.length > 0 && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs text-gray-400">Suggestions:</span>
                            {fileSuggestions.map((tag) => (
                              <button
                                key={tag}
                                onClick={() => addFileTag(fileItem.id, tag)}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                                disabled={isUploading}
                              >
                                <PlusIcon className="h-2.5 w-2.5 mr-0.5" />
                                {tag}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Supported file types */}
          <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded space-y-2">
            <h5 className="font-medium text-gray-700">Supported file types:</h5>
            <p>
              <strong>Images:</strong> JPG, PNG, GIF, WebP, SVG (max 10MB)
            </p>
            <p>
              <strong>Videos:</strong> MP4, WebM, MOV, AVI (max 250MB)
            </p>
            <p>
              <strong>Documents:</strong> PDF (max 250MB)
            </p>
            <p>
              <strong>Audio:</strong> PDF (max 250MB)
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={validFiles.length === 0 || isUploading}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Uploading...
              </span>
            ) : (
              <>Upload{validFiles.length > 0 ? `(${validFiles.length})` : ''}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetUploadModal;
